"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ImageUpload,
  type MediaUploadScope,
  type MediaUploadState,
  type UploadedMedia,
} from "@/components/image-upload";
import {
  type MediaAsset,
  type MediaTag,
  normalizeMediaArray,
} from "@/lib/media";
import {
  isNativePickerAvailable,
  pickPostMedia,
} from "@/lib/native/photo-picker";
import { MediaTagEditor } from "./media-tag-editor.client";

type PostMediaComposerProps = {
  maxImages?: number;
  acceptVideos?: boolean;
  onImagesChange?: (images: UploadedMedia[]) => void;
  onUploadingChange?: (isUploading: boolean) => void;
  onUploadStateChange?: (state: MediaUploadState) => void;
  initialImages?: MediaAsset[];
  uploadScope?: MediaUploadScope;
  className?: string;
  currentUserId?: string;
  enableTagging?: boolean;
  dialogLayer?: number;
};

const omitTags = (image: UploadedMedia): UploadedMedia => {
  if (!image.tags) {
    return image;
  }
  const { tags: _tags, ...rest } = image;
  return rest;
};

/**
 * Full-screen media step for the post composer. Wraps ImageUpload (the upload
 * pipeline: multi-select, reorder, crop) and layers Instagram-style per-image
 * people tagging on top.
 *
 * Tags live in an id-keyed side map and are merged into the emitted media on
 * every change, so they survive reorder (id stable) and disappear when an
 * image is removed (id gone) — without forking ImageUpload's pipeline.
 */
export function PostMediaComposer({
  maxImages = 6,
  acceptVideos = true,
  onImagesChange,
  onUploadingChange,
  onUploadStateChange,
  initialImages = [],
  uploadScope,
  className,
  currentUserId,
  enableTagging = true,
  dialogLayer,
}: PostMediaComposerProps) {
  // Seeded from `initialImages` so a remount (the composer's media sheet
  // unmounts while closed) starts from the draft instead of emitting an empty
  // list — and keeps the people tags already placed on it.
  const [images, setImages] = useState<UploadedMedia[]>(() =>
    normalizeMediaArray(initialImages),
  );
  const [tagsByMediaId, setTagsByMediaId] = useState<
    Record<string, MediaTag[]>
  >(() => {
    const seeded: Record<string, MediaTag[]> = {};
    for (const image of normalizeMediaArray(initialImages)) {
      if (image.tags?.length) seeded[image.id] = image.tags;
    }
    return seeded;
  });
  const [taggingImageId, setTaggingImageId] = useState<string | null>(null);

  const onImagesChangeRef = useRef(onImagesChange);
  onImagesChangeRef.current = onImagesChange;

  // Merge id-keyed tags into the emitted media whenever images or tags change.
  useEffect(() => {
    const merged = images.map((image) => {
      const tags = tagsByMediaId[image.id];
      if (tags && tags.length > 0 && image.kind === "image") {
        return { ...image, tags };
      }
      return omitTags(image);
    });
    onImagesChangeRef.current?.(merged);
  }, [images, tagsByMediaId]);

  const handleImagesChange = useCallback((next: UploadedMedia[]) => {
    setImages(next);
    setTagsByMediaId((previous) => {
      const presentIds = new Set(next.map((item) => item.id));
      const pruned: Record<string, MediaTag[]> = {};
      let changed = false;
      for (const [id, tags] of Object.entries(previous)) {
        if (presentIds.has(id)) {
          pruned[id] = tags;
        } else {
          changed = true;
        }
      }
      return changed ? pruned : previous;
    });
  }, []);

  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [id, tags] of Object.entries(tagsByMediaId)) {
      if (tags.length > 0) {
        counts[id] = tags.length;
      }
    }
    return counts;
  }, [tagsByMediaId]);

  const taggingImage = taggingImageId
    ? (images.find((image) => image.id === taggingImageId) ?? null)
    : null;

  // On native, route the add action through the OS gallery picker; web keeps
  // the file input.
  const nativePicker = useMemo(() => {
    if (!isNativePickerAvailable()) {
      return undefined;
    }
    return (limit: number) => pickPostMedia({ limit });
  }, []);

  return (
    <>
      <ImageUpload
        variant="feed"
        maxImages={maxImages}
        acceptVideos={acceptVideos}
        onImagesChange={handleImagesChange}
        onUploadingChange={onUploadingChange}
        onUploadStateChange={onUploadStateChange}
        initialImages={initialImages}
        uploadScope={uploadScope}
        className={className}
        showReorderHint
        enableCropping
        onTagImage={enableTagging ? setTaggingImageId : undefined}
        tagCounts={enableTagging ? tagCounts : undefined}
        nativePicker={nativePicker}
        dialogLayer={dialogLayer}
      />

      {enableTagging && taggingImage?.url ? (
        <MediaTagEditor
          open={taggingImageId !== null}
          onOpenChange={(next) => {
            if (!next) {
              setTaggingImageId(null);
            }
          }}
          imageUrl={taggingImage.url}
          tags={tagsByMediaId[taggingImage.id] ?? []}
          currentUserId={currentUserId}
          onChange={(tags) =>
            setTagsByMediaId((previous) => ({
              ...previous,
              [taggingImage.id]: tags,
            }))
          }
        />
      ) : null}
    </>
  );
}
